import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { CreateOrUpdateDataSetSchema } from 'libs/end-to-end/src/types/databases/data-sets/create-or-update-data-set-request-body';
import { DataSetType } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { DataSetTypes } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { IntegrationType } from 'libs/end-to-end/src/types/databases/integrations/integration-type';
import { IntegrationTypes } from 'libs/end-to-end/src/types/databases/integrations/integration-type';
import { SupplementalDatasetType } from 'libs/end-to-end/src/datasets';
import { SupplementalDatasetTypes } from 'libs/end-to-end/src/datasets';
import { Database } from 'libs/end-to-end/src/types/databases/database';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { SaveIndicatorStatus } from 'apps/sigma2-ui/src/components/SaveIndicator';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import { ListItem } from 'libs/end-to-end/src/types/generic/generic';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { createDefaultDataSetConfig } from 'libs/end-to-end/src/types/databases/data-sets/create-default-data-set-config';
import { MergeCategoryEnum } from 'libs/end-to-end/src/merge';
import { ResumeQualityScoreRubric } from 'libs/end-to-end/src/types/resume-parser/resume-quality-score-rubric';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';

export interface DataSetNewPageHook {
  organizationId: string;
  databaseId: string;
  databaseName: string;
  submitButtonLabel: string;
  submitButtonBehavior: TouchTargetBehavior;
  submitButtonDisabled: boolean;
  saveIndicatorStatus: SaveIndicatorStatus;
  backButtonLabel: string;
  backButtonBehavior: TouchTargetBehavior;
  backButtonDisabled: boolean;
  displayNameLabel: string;
  displayName: string;
  setDisplayName: Setter<string>;
  type: DataSetType;
  setType: Setter<DataSetType>;
  types: ListItem<DataSetType>[];
  typeLabel: string;
  integrationType: IntegrationType;
  setIntegrationType: Setter<IntegrationType>;
  integrationTypes: ListItem<IntegrationType>[];
  integrationTypeLabel: string;
  supplementalDataSetType: SupplementalDatasetType;
  setSupplementalDataSetType: Setter<SupplementalDatasetType>;
  supplementalDataSetTypes: ListItem<SupplementalDatasetType>[];
  supplementalDataSetTypeLabel: string;
  mergeCategory: string;
  setMergeCategory: Setter<MergeCategoryEnum>;
  resumeGradingRubric: ResumeQualityScoreRubric[];
  setResumeGradingRubric: Setter<ResumeQualityScoreRubric[]>;
  isResumeGradingEnabled: boolean;
  setIsResumeGradingEnabled: Setter<boolean>;
  openAiModel: AllowedOpenAiModels;
  setOpenAiModel: Setter<AllowedOpenAiModels>;
}

export const useDataSetNewPage = (): DataSetNewPageHook => {
  const { organizationId, databaseId } = useParams();
  const navigate = useNavigateSafe();

  if (!organizationId || !databaseId) {
    throw new Error('organizationId and databaseId are required');
  }

  const [displayName, setDisplayName] = useState('');
  const [type, setType] = useState<DataSetType>('File');
  const [mergeCategory, setMergeCategory] = useState<MergeCategoryEnum>('hris');
  const [integrationType, setIntegrationType] =
    useState<IntegrationType>('ShareFile');
  const [supplementalDataSetType, setSupplementalDataSetType] =
    useState<SupplementalDatasetType>('external_pay_benchmarking');
  const [resumeGradingRubric, setResumeGradingRubric] = useState<
    ResumeQualityScoreRubric[]
  >([]);
  const [isResumeGradingEnabled, setIsResumeGradingEnabled] = useState(false);
  const [openAiModel, setOpenAiModel] =
    useState<AllowedOpenAiModels>('gpt-4o-mini');

  const readDatabaseGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}`;
  }, [organizationId, databaseId]);

  const readDatabasePattern = useSimpleReadPattern({
    getUrlFn: readDatabaseGetUrlFn,
    responseSchema: Database,
    canRead: true,
  });

  const validFormState =
    displayName.length > 0 &&
    !(
      type === 'Supplemental' &&
      supplementalDataSetType === 'resumes' &&
      isResumeGradingEnabled &&
      resumeGradingRubric.length === 0
    );

  const patternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const pattern = useSimpleWritePattern({
    writeSchema: CreateOrUpdateDataSetSchema,
    responseSchema: DataSet,
    getUrlFn: patternGetUrlFn,
    canWrite: validFormState,
    method: 'POST',
  });

  useEffect(() => {
    if (pattern.status === 'done' && pattern.saved && pattern.entity) {
      navigate(
        `/organizations/${organizationId}/databases/${databaseId}/data-sets/edit/${pattern.entity.id}`,
      );
    }
  }, [pattern, navigate, organizationId, databaseId]);

  return useMemo(() => {
    return {
      organizationId,
      databaseId,
      displayNameLabel: 'Display Name',
      displayName,
      setDisplayName,
      supplementalDataSetType,
      integrationTypeLabel: 'Integration Type',
      setSupplementalDataSetType,
      setIntegrationType,
      setType,
      supplementalDataSetTypeLabel: 'Supplemental Data Set Type',
      type,
      integrationType,
      typeLabel: 'Data Set Type',
      types: DataSetTypes.map((type) => ({
        optionLabel: type,
        optionValue: type,
      })),
      supplementalDataSetTypes: SupplementalDatasetTypes.map((type) => ({
        optionLabel: type,
        optionValue: type,
      })),
      integrationTypes: IntegrationTypes.map((type) => ({
        optionLabel: type,
        optionValue: type,
      })),
      databaseName:
        readDatabasePattern.status === 'done'
          ? readDatabasePattern.entity.name
          : '',
      saveIndicatorStatus: pattern.saveIndicatorStatus,
      submitButtonDisabled: !pattern.canWrite || !validFormState,
      submitButtonLabel: 'Create Data Set',
      submitButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          if (
            !pattern.canWrite ||
            (type === 'Supplemental' &&
              supplementalDataSetType === 'resumes' &&
              isResumeGradingEnabled &&
              resumeGradingRubric.length === 0)
          ) {
            return;
          }

          pattern.saveEntity({
            displayName,
            config: createDefaultDataSetConfig({
              type,
              integrationType,
              supplementalDataSetType,
              category: mergeCategory,
            }),
            isArchived: false,
          });
        },
      },
      backButtonDisabled: pattern.status === 'saving',
      backButtonLabel: 'Back',
      backButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          navigate(
            `/organizations/${organizationId}/databases/${databaseId}/data-sets`,
          );
        },
      },
      mergeCategory,
      setMergeCategory,
      resumeGradingRubric,
      setResumeGradingRubric,
      isResumeGradingEnabled,
      setIsResumeGradingEnabled,
      openAiModel,
      setOpenAiModel,
    };
  }, [
    displayName,
    integrationType,
    supplementalDataSetType,
    pattern,
    readDatabasePattern,
    type,
    validFormState,
    navigate,
    organizationId,
    databaseId,
    mergeCategory,
    setMergeCategory,
    resumeGradingRubric,
    isResumeGradingEnabled,
    openAiModel,
  ]);
};
